/* DashboardAnalytics.jsx
 * Three connected widgets for the Dashboard:
 *  - CategoryBreakdown: Alkollü/Alkolsüz/Soft daily consumption chart
 *  - TopProducts: Top N most-consumed products
 *  - AutoRequests: Guest-indexed replenishment suggestions
 *
 * All widgets react to a shared selectedBar filter (set via barId prop).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { Trophy, Package, Sparkles, RefreshCw, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { AIAgentWidget } from './AIAgentWidget';

const CAT_COLORS = {
    'Alkollü': '#dc2626',
    'Alkolsüz': '#2563eb',
    'Soft': '#16a34a',
    'Diğer': '#94a3b8',
};

export function CategoryBreakdown({ barId, days = 7 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = { days };
            if (barId) params.bar_id = barId;
            const res = await api.get('/analytics/category-breakdown', { params });
            setData(res.data);
        } catch (e) {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [barId, days]);

    useEffect(() => { fetchData(); }, [fetchData]);

    return (
        <Card data-testid="widget-category-breakdown">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Package className="w-5 h-5 text-orwion-green" />
                    Kategori Karşılaştırma {barId ? '(seçili bar)' : '(tüm barlar)'}
                </CardTitle>
                <CardDescription>Son {days} günde Alkollü / Alkolsüz / Soft tüketim dağılımı</CardDescription>
            </CardHeader>
            <CardContent>
                {loading && <div className="text-sm text-slate-400 p-4">Yükleniyor...</div>}
                {!loading && data && (() => {
                    const totalConsumption = data.summary.reduce((s, x) => s + (x.consumption || 0), 0);
                    if (totalConsumption === 0) {
                        return (
                            <div className="p-6 text-center bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                                <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                                <div className="text-sm font-medium text-slate-600 mb-1">
                                    Bu {barId ? 'barda' : 'aralıkta'} henüz tüketim verisi yok
                                </div>
                                <div className="text-xs text-slate-400">
                                    Bar sayım sayfasında "Bugün Sayım" değerleri girildikçe grafik dolacak
                                </div>
                            </div>
                        );
                    }
                    return (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                            {data.summary.map((s) => (
                                <div key={s.category} className="rounded-lg border p-2 text-center"
                                    style={{ borderColor: CAT_COLORS[s.category], background: `${CAT_COLORS[s.category]}10` }}>
                                    <div className="text-xs text-slate-500">{s.category}</div>
                                    <div className="text-sm font-bold" style={{ color: CAT_COLORS[s.category] }}>
                                        {s.consumption} adet
                                    </div>
                                    <div className="text-[10px] text-slate-400">{s.cost_tl.toLocaleString('tr-TR')} TL</div>
                                </div>
                            ))}
                        </div>
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={data.daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: '12px' }} />
                                <Bar dataKey="Alkollü" stackId="a" fill={CAT_COLORS['Alkollü']} />
                                <Bar dataKey="Alkolsüz" stackId="a" fill={CAT_COLORS['Alkolsüz']} />
                                <Bar dataKey="Soft" stackId="a" fill={CAT_COLORS['Soft']} />
                                <Bar dataKey="Diğer" stackId="a" fill={CAT_COLORS['Diğer']} />
                            </BarChart>
                        </ResponsiveContainer>
                    </>
                    );
                })()}
            </CardContent>
        </Card>
    );
}

export function TopProducts({ barId, days = 7, limit = 10 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('consumption'); // 'consumption' | 'cost'

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = { days, limit };
            if (barId) params.bar_id = barId;
            const res = await api.get('/analytics/top-products', { params });
            setData(res.data);
        } catch (e) {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [barId, days, limit]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const PIE_COLORS = ['#dc2626', '#2563eb', '#16a34a', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#64748b', '#10b981', '#f97316'];
    
    const chartData = (data?.top || []).map((p, i) => ({
        name: p.product_name.length > 18 ? p.product_name.slice(0, 16) + '...' : p.product_name,
        fullName: p.product_name,
        value: view === 'consumption' ? p.consumption : p.cost_tl,
        unit: p.unit,
        category: p.category,
        consumption: p.consumption,
        cost_tl: p.cost_tl,
        color: PIE_COLORS[i % PIE_COLORS.length],
    }));

    return (
        <Card data-testid="widget-top-products">
            <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Trophy className="w-5 h-5 text-amber-500" />
                            En Çok Tüketilen Ürünler {barId ? '(seçili bar)' : '(genel)'}
                        </CardTitle>
                        <CardDescription>Son {days} günün toplamı, ilk {limit} ürün</CardDescription>
                    </div>
                    <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                        <button
                            onClick={() => setView('consumption')}
                            className={`px-3 py-1 text-xs rounded-md transition ${view === 'consumption' ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-500'}`}
                            data-testid="top-view-consumption"
                        >
                            Adet
                        </button>
                        <button
                            onClick={() => setView('cost')}
                            className={`px-3 py-1 text-xs rounded-md transition ${view === 'cost' ? 'bg-white shadow text-slate-900 font-semibold' : 'text-slate-500'}`}
                            data-testid="top-view-cost"
                        >
                            TL
                        </button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {loading && <div className="text-sm text-slate-400 p-4">Yükleniyor...</div>}
                {!loading && data && data.top.length === 0 && (
                    <div className="text-sm text-slate-400 p-4 text-center">Henüz tüketim verisi yok</div>
                )}
                {!loading && data && data.top.length > 0 && (
                    <div className="space-y-3">
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={85}
                                    innerRadius={35}
                                    paddingAngle={2}
                                    dataKey="value"
                                    nameKey="name"
                                    label={(e) => `${e.name}`}
                                    labelLine={false}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(v, n, p) => {
                                        const d = p.payload;
                                        return view === 'consumption'
                                            ? [`${d.consumption} ${d.unit}`, d.fullName]
                                            : [`${d.cost_tl.toLocaleString('tr-TR')} TL`, d.fullName];
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-1 max-h-48 overflow-auto pt-2 border-t border-slate-100">
                            {data.top.map((p, idx) => (
                                <div key={p.product_name + idx} className="flex items-center justify-between text-xs py-1" data-testid={`top-product-${idx}`}>
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                                        <span className="font-mono text-[10px] text-slate-400 w-5">{idx + 1}.</span>
                                        <span className="font-medium text-slate-700 truncate">{p.product_name}</span>
                                    </div>
                                    <div className="text-right ml-2 flex-shrink-0">
                                        {view === 'consumption' ? (
                                            <><span className="font-bold text-slate-800">{p.consumption}</span><span className="text-slate-400 ml-1">{p.unit}</span></>
                                        ) : (
                                            <span className="font-bold text-emerald-700">{p.cost_tl.toLocaleString('tr-TR')} TL</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export function AutoRequests({ barId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expectedGuests, setExpectedGuests] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async () => {
        if (!barId) {
            setData(null);
            return;
        }
        setLoading(true);
        try {
            const params = { bar_id: barId };
            if (expectedGuests) params.expected_guests = expectedGuests;
            const res = await api.get('/analytics/auto-requests', { params });
            setData(res.data);
        } catch (e) {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [barId, expectedGuests]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSaveAsRequest = async () => {
        if (!data?.suggestions?.length) return;
        setSaving(true);
        try {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowStr = tomorrow.toISOString().split('T')[0];
            const items = data.suggestions.map(s => ({
                product_name: s.product_name,
                unit: s.unit,
                quantity: s.suggested_quantity,
            }));
            await api.post('/bar-requests', {
                bar_id: barId,
                date: tomorrowStr,
                items,
                notes: `Otomatik öneri (${data.expected_guests} misafir)`,
            });
            toast.success(`${items.length} ürün ${tomorrowStr} için Talepler'e eklendi`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Talep kaydedilemedi');
        } finally {
            setSaving(false);
        }
    };

    if (!barId) {
        return (
            <Card data-testid="widget-auto-requests">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Sparkles className="w-5 h-5 text-purple-500" />
                        Otomatik Talep Önerisi
                    </CardTitle>
                    <CardDescription>Bar seçtiğinizde, son 7 günün misafir başına tüketimine göre yarın için öneri hesaplanır</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="text-sm text-slate-400 p-6 text-center bg-slate-50 rounded-lg">
                        Lütfen yukarıdaki "Bar Bazlı Maliyet Dağılımı"ndan bir bar seçin
                    </div>
                </CardContent>
            </Card>
        );
    }

    const totalCost = (data?.suggestions || []).reduce((s, x) => s + (x.estimated_cost_tl || 0), 0);

    return (
        <Card data-testid="widget-auto-requests">
            <CardHeader>
                <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Sparkles className="w-5 h-5 text-purple-500" />
                            Otomatik Talep Önerisi
                        </CardTitle>
                        <CardDescription>
                            {data?.bar_name} için yarınki misafir sayısına göre önerilen çekiliş listesi
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            placeholder={data?.expected_guests || 'Misafir'}
                            value={expectedGuests}
                            onChange={(e) => setExpectedGuests(e.target.value)}
                            className="w-28 h-8 text-sm"
                            data-testid="expected-guests-input"
                        />
                        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-suggestions-btn">
                            <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {loading && <div className="text-sm text-slate-400 p-4">Hesaplanıyor...</div>}
                {!loading && data && (
                    <>
                        <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                            <div className="bg-slate-50 rounded p-2">
                                <div className="text-[10px] text-slate-500">Tahmini Misafir</div>
                                <div className="font-bold text-slate-700">{data.expected_guests}</div>
                            </div>
                            <div className="bg-slate-50 rounded p-2">
                                <div className="text-[10px] text-slate-500">Öneri Sayısı</div>
                                <div className="font-bold text-purple-700">{data.suggestions.length}</div>
                            </div>
                            <div className="bg-slate-50 rounded p-2">
                                <div className="text-[10px] text-slate-500">Tahmini Maliyet</div>
                                <div className="font-bold text-emerald-700">{totalCost.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL</div>
                            </div>
                        </div>
                        {data.suggestions.length === 0 ? (
                            <div className="text-sm text-slate-400 p-4 text-center bg-amber-50 rounded">
                                Yeterli geçmiş veri yok veya stok yeterli. Misafir sayısını veya geçmiş günleri kontrol edin.
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-end mb-2">
                                    <Button
                                        size="sm"
                                        onClick={handleSaveAsRequest}
                                        disabled={saving}
                                        className="bg-purple-600 hover:bg-purple-700"
                                        data-testid="save-as-request-btn"
                                    >
                                        <ClipboardList className="w-4 h-4 mr-1.5" />
                                        {saving ? 'Kaydediliyor...' : `Yarına Talep Olarak Kaydet (${data.suggestions.length})`}
                                    </Button>
                                </div>
                                <div className="space-y-1 max-h-80 overflow-auto">
                                {data.suggestions.map((s, idx) => (
                                    <div key={s.product_name + idx}
                                        className="flex items-center justify-between p-2 bg-purple-50/50 border border-purple-100 rounded"
                                        data-testid={`suggestion-${idx}`}>
                                        <div>
                                            <div className="font-medium text-sm text-slate-800">{s.product_name}</div>
                                            <div className="text-[10px] text-slate-500">
                                                Mevcut: {s.on_hand} {s.unit} · Tahmin: {s.forecast} {s.unit}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-bold text-purple-700">{s.suggested_quantity} {s.unit}</div>
                                            <div className="text-[10px] text-slate-500">{s.estimated_cost_tl.toLocaleString('tr-TR')} TL</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            </>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export function DashboardAnalytics({ barId }) {
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <CategoryBreakdown barId={barId} days={7} />
                <TopProducts barId={barId} days={7} limit={10} />
            </div>
            <AIAgentWidget barId={barId} />
        </div>
    );
}

export default DashboardAnalytics;
